import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { GenericEntry } from "@shared/schema";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { FIELD_KEYS } from "@shared/constants/fields";
import { useSectionFields } from "./useSectionFields";

/**
 * INTERVIEW-SHELL-1 (s205, SHELL-PILOT-1) — the chapter checkpoint frame.
 *
 * §4B 8 / D-202.16: a checkpoint shows CONSEQUENCES, not counts — "a consequence, not a second
 * question". The count line is the one number that already moved in the header; the card underneath
 * says what is now true, or what is still missing, in the planner's terms.
 *
 * The pilot ships ONE consequence card so the frame is real and measurable. The card is DATA-DRIVEN
 * (it reads whether a guardian is actually named), not a static string — a checkpoint that asserted a
 * consequence without checking it would be the "narrate what the system recorded" pattern Reade cut at
 * s203 (§4B 18), and worse, it could assert something false.
 */
export function CheckpointScreen({
  clientId,
  total,
  ready,
}: {
  clientId: string;
  total: number;
  ready: number;
}) {
  // 🔴 s205 FIX. This card asserted a FALSEHOOD on staging — "No one is named to look after your
  // pets yet" while the planner's guardian was in the database — through two independent bugs, both
  // of which had to go:
  //
  //  1. the request hardcoded `categoryType=personal`; the enum value is `personal_info`, so it
  //     returned 500 and `entries` was permanently `[]`. The type is now RESOLVED from the category
  //     record (`useSectionFields`), so there is no literal left to get wrong;
  //  2. the match read `e.fieldKey`, which this payload does not carry — it carries `fieldId`. A
  //     `(e as { fieldKey?: string })` cast is what made the compiler accept a property that does not
  //     exist, which is why `tsc` stayed green over a false statement (F-199.27's family). The cast is
  //     DELETED and the match is on the resolved `fieldId`; fixing the enum alone would not have
  //     fixed the card.
  const { categoryType, sectionId, fieldIdByKey } = useSectionFields(
    clientId,
    CATEGORY_SLUGS.PERSONAL_INFORMATION,
    SECTION_SLUGS.FAMILY_PETS,
  );
  const guardianFieldId = fieldIdByKey.get(FIELD_KEYS.GUARDIAN);

  const { data: entries = [] } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", categoryType],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`);
      return r.json();
    },
    enabled: !!clientId && !!categoryType,
  });

  // Scoped to the pets section and to a visible, non-empty cell: a hidden row is not a named
  // guardian, and neither is a blank one.
  const guardianNamed = useMemo(
    () =>
      !!guardianFieldId &&
      entries.some(
        (e) =>
          e.fieldId === guardianFieldId &&
          e.sectionId === sectionId &&
          e.isHidden !== true &&
          (e.value ?? "").trim() !== "",
      ),
    [entries, guardianFieldId, sectionId],
  );

  return (
    <div data-testid="interview-checkpoint">
      <p className="text-[15px] text-foreground/90" data-testid="interview-checkpoint-count">
        Your executor's list stands at <strong>{total}</strong> jobs, <strong>{ready}</strong> of them
        ready to go.
      </p>

      <div
        className="mt-4 rounded-lg border border-warm-stone bg-white p-4"
        data-testid="interview-checkpoint-card"
      >
        {/* s224 Lane F (D-224.g) — the two sub-lines were `[SPEC copy pending]` placeholders. Reade
            ruled the BOARD's words in (RULE 137 / BOARD-AS-SPEC-1): both sentences are the pets
            consequence card's own, Main.dc.html:1689-1690, split by the branch each one is true in.
            🔴 They are HAND-TYPED because the board states this card as imperative JS inside its
            render function, so `extractSteps` cannot reach it and `boardWords` cannot carry it — the
            generator-side fix, and the rest of the board's ~15 cards, are the deferred Act 2 build. */}
        {guardianNamed ? (
          <>
            <p className="text-[14px] font-medium text-foreground">
              Someone is named to look after your pets
            </p>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Get the animal to the person you named.
            </p>
          </>
        ) : (
          <>
            <p className="text-[14px] font-medium text-foreground">
              No one is named to look after your pets yet
            </p>
            <p className="mt-1 text-[13px] text-muted-foreground">
              The one task that cannot wait a week. A name here turns a crisis into a phone
              call.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
